// App related enums and interfaces
export enum AppCode {
  YOGA_GO = 'YG',
  FEAT_COACH = 'FC',
  MUSCLE_BOOSTER = 'MB',
  FASTEASY = 'FE',
  OMO = 'OM',
  WALKFIT = 'WF',
  FOTTUR = 'FT',
  RELIFY = 'RL',
  FEMIA = 'FM',
  MINDEA = 'MI',
}

export interface App {
  name: string;
  code: AppCode;
}

export const APP_LIST: App[] = [
  { name: 'Muscle Booster', code: AppCode.MUSCLE_BOOSTER },
  { name: 'YogaGo', code: AppCode.YOGA_GO },
  { name: 'WalkFit', code: AppCode.WALKFIT },
];

// Platform related enums and interfaces
export enum Platform {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
}

export interface PlatformOption {
  name: string;
  code: Platform;
}

type CallToActionCode =
  'DOWNLOAD'
  | 'USE_APP'
  | 'INSTALL_MOBILE_APP'
  | 'LEARN_MORE'
  | 'WATCH_MORE'
  | 'ORDER_NOW'
  | 'BUY_NOW'
  | 'OPEN_LINK';

interface CallToAction {
  name: string;
  code: CallToActionCode;
}

export const CALL_TO_ACTION: CallToAction[] = [
  { name: 'Download', code: 'DOWNLOAD' },
  { name: 'Use App', code: 'USE_APP' },
  { name: 'Install Mobile App', code: 'INSTALL_MOBILE_APP' },
  { name: 'Learn More', code: 'LEARN_MORE' },
  { name: 'Watch More', code: 'WATCH_MORE' },
  { name: 'Order Now', code: 'ORDER_NOW' },
  { name: 'Buy Now', code: 'BUY_NOW' },
  { name: 'Open Link', code: 'OPEN_LINK' },
];

export const APP_PLATFORMS: PlatformOption[] = [
  { name: 'Web', code: Platform.WEB },
  { name: 'Mobile', code: Platform.MOBILE },
];

// Marketing process related enums and interfaces
export enum MarketingProcessCode {
  // INBOX = 'inbox',
  TESTING = 'testing',
  SCALING = 'scaling',
  SCALING_EG = 'scaling_eg',
  HYPOTHESIS_TESTING = 'hypothesis_testing',
}

export interface MarketingProcess {
  name: string;
  code: MarketingProcessCode;
}

export const MARKETING_PROCESS: MarketingProcess[] = [
  { name: 'Scaling', code: MarketingProcessCode.SCALING },
  { name: 'Scaling EG', code: MarketingProcessCode.SCALING_EG },
  { name: 'Hypothesis testing', code: MarketingProcessCode.HYPOTHESIS_TESTING },
  { name: 'Testing', code: MarketingProcessCode.TESTING },
];

// TikTok ads related enums and interfaces
export enum TikTokAdType {
  SMART_AD = 'SMART_AD',
  SINGLE_VIDEO = 'SINGLE_VIDEO',
}

export interface TikTokAd {
  name: string;
  code: TikTokAdType;
}

export const TIKTOK_ADS_AD_LIST: TikTokAd[] = [
  { name: 'Smart Ad', code: TikTokAdType.SMART_AD },
  { name: 'Single Video Ad', code: TikTokAdType.SINGLE_VIDEO },
];

// Demographics related enums and interfaces
export enum AgeRange {
  AGE_13_17 = 'AGE_13_17',
  AGE_18_24 = 'AGE_18_24',
  AGE_25_34 = 'AGE_25_34',
  AGE_35_44 = 'AGE_35_44',
  AGE_45_54 = 'AGE_45_54',
  AGE_55_100 = 'AGE_55_100',
}

export interface AgeRangeOption {
  value: AgeRange;
  label: string;
}

export const AGE_RANGE_ITEMS: AgeRangeOption[] = [
  { value: AgeRange.AGE_13_17, label: '13-17' },
  { value: AgeRange.AGE_18_24, label: '18-24' },
  { value: AgeRange.AGE_25_34, label: '25-34' },
  { value: AgeRange.AGE_35_44, label: '35-44' },
  { value: AgeRange.AGE_45_54, label: '45-54' },
  { value: AgeRange.AGE_55_100, label: '55+' },
];

export enum SpendingPower {
  ALL = 'ALL',
  HIGH = 'HIGH',
}

export interface SpendingPowerOption {
  value: SpendingPower;
  label: string;
}

export const SPENDING_POWER_ITEMS: SpendingPowerOption[] = [
  { value: SpendingPower.ALL, label: 'All SP' },
  { value: SpendingPower.HIGH, label: 'High SP' },
];

export enum Gender {
  FEMALE = 'GENDER_FEMALE',
  MALE = 'GENDER_MALE',
}

export interface GenderOption {
  value: Gender;
  label: string;
}

export const GENDER_ITEMS: GenderOption[] = [
  { value: Gender.FEMALE, label: 'Female' },
  { value: Gender.MALE, label: 'Male' },
];

export enum Team {
  MM = 'MM',
  UA = 'UA',
}

export interface TeamOption {
  value: Team;
  label: string;
}

export const TEAM_ITEMS: TeamOption[] = [
  { value: Team.MM, label: 'MM' },
  { value: Team.UA, label: 'UA' },
];

// Bid strategy related enums and interfaces
export const DEFAULT_TARGET_CPA = '65.00';
export const DEFAULT_TARGET_ROAS = '1.00';

export enum BidStrategyType {
  HIGHEST_VALUE = 'HV',
  LOWEST_COST = 'LC',
  ROAS = 'ROAS',
  CPA = 'tCPA',
}

export interface BidStrategy {
  name: string;
  value: BidStrategyType;
}

export const BID_STRATEGIES_LIST: BidStrategy[] = [
  { name: 'Highest Value', value: BidStrategyType.HIGHEST_VALUE },
  { name: 'Lowest Cost', value: BidStrategyType.LOWEST_COST },
  { name: 'ROAS', value: BidStrategyType.ROAS },
  { name: 'CPA', value: BidStrategyType.CPA },
];

export interface BidStrategyConfig {
  bid_type: string;
  deep_bid_type: string;
  select_bid_type: BidStrategyType;
  input_value: string | null;
  optimization_goal: 'VALUE' | 'CONVERT';
}

export const BID_STRATEGIES: Record<BidStrategyType, BidStrategyConfig> = {
  [BidStrategyType.HIGHEST_VALUE]: {
    bid_type: 'BID_TYPE_NO_BID',
    deep_bid_type: 'VO_HIGHEST_VALUE',
    select_bid_type: BidStrategyType.HIGHEST_VALUE,
    input_value: null,
    optimization_goal: 'VALUE',
  },
  [BidStrategyType.LOWEST_COST]: {
    bid_type: 'BID_TYPE_NO_BID',
    deep_bid_type: 'DEFAULT',
    select_bid_type: BidStrategyType.LOWEST_COST,
    input_value: null,
    optimization_goal: 'CONVERT',
  },
  [BidStrategyType.CPA]: {
    bid_type: 'BID_TYPE_CUSTOM',
    deep_bid_type: 'PACING',
    select_bid_type: BidStrategyType.CPA,
    input_value: DEFAULT_TARGET_CPA,
    optimization_goal: 'CONVERT',
  },
  [BidStrategyType.ROAS]: {
    bid_type: 'BID_TYPE_NO_BID',
    deep_bid_type: 'VO_MIN_ROAS',
    select_bid_type: BidStrategyType.ROAS,
    input_value: DEFAULT_TARGET_ROAS,
    optimization_goal: 'VALUE',
  },
};

// Locale related enums
export enum Locale {
  ENGLISH = 'en',
  RUSSIAN = 'ru',
  UKRAINIAN = 'ua',
  GERMAN = 'de',
  FRENCH = 'fr',
  SPANISH = 'es',
  ITALIAN = 'it',
  PORTUGUESE = 'pt',
  POLISH = 'pl',
  JAPANESE = 'jp',
  KOREAN = 'kr',
  CHINESE = 'ch',
  CHINESE_TRADITIONAL = 'CN',
  ARABIC = 'ar',
  DUTCH = 'nl',
  INDONESIAN = 'id',
  TURKISH = 'tr',
  THAI = 'th',
  UNITED_KINGDOM = 'uk',

  NORWEGIAN = 'no',
  DANISH = 'da',
  FINNISH = 'fi',
  SWEDISH = 'sv',
  CZECH = 'cs',
  ROMANIAN = 'ro',
  HUNGARIAN = 'hu',
  GREEK = 'el',
  SLOVAK = 'sk',
  BULGARIAN = 'bg',
  CROATIAN = 'hr',
  SLOVENE = 'sl',
  LITHUANIAN = 'lt',
  ESTONIAN = 'et',
  LATVIAN = 'lv',

  UNIVERSAL = 'universal',
  UNKNOWN = 'unknown',
}

// For backward compatibility
export const WEB_PLATFORM = Platform.WEB;
export const MOBILE_PLATFORM = Platform.MOBILE;
export const CPA_VALUE = BidStrategyType.CPA;
export const ROAS_VALUE = BidStrategyType.ROAS;
export const LOWEST_VALUE = BidStrategyType.LOWEST_COST;
export const HIGHEST_VALUE = BidStrategyType.HIGHEST_VALUE;
export const LOCALES = Locale;

export enum Network {
  FACEBOOK = 'Facebook',
  SNAPCHAT = 'Snapchat',
  TIKTOK = 'Tik Tok',
  INSTAGRAM = 'Instagram',
  GOOGLE = 'Google'
}

export interface NetworkOption {
  name: string;
  code: Network;
}

export const NETWORK_LIST: NetworkOption[] = [
  { name: Network.FACEBOOK, code: Network.FACEBOOK },
  { name: Network.SNAPCHAT, code: Network.SNAPCHAT },
  { name: Network.TIKTOK, code: Network.TIKTOK },
  { name: Network.INSTAGRAM, code: Network.INSTAGRAM },
  { name: Network.GOOGLE, code: Network.GOOGLE },
];

export const DOC_WEB_SERVICE = 'https://welltech.atlassian.net/wiki/spaces/MA1/pages/4433772806/Web+Service';
export const DOC_WIMAS = 'https://welltech.atlassian.net/wiki/spaces/MA1/pages/5120098319/WIMAS+Our+New+Incident+Management+System';
